import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Wallet,
  Percent,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Download,
  ListChecks,
  Plus,
  Trash2,
} from "lucide-react";
import { cn, downloadBlob, parseBlobError, formatEur } from "@/lib/utils";
import { NIVEL_PROYECCION_LABEL } from "@/components/proyeccion/GeneralesContent";
import { TABLA_ANIOS, ColsAnios, COL_ANIO_MONO } from "@/components/ui/tabla-anios";
import { CHART, axisProps, gridProps, ChartTooltip, ChartLegend, barCursor, lineCursor, fmtCompact } from "@/components/ui/chart-kit";
import {
  getProyeccionResultado,
  getDfn,
  getProyeccionConfig,
  saveProyeccionConfig,
  exportarProyeccion,
} from "@/lib/api";

// Color del histórico según tema (ver components/ui/chart-kit)
const GUNMETAL = CHART.historico;

// Formato único de proyección: una sola fuente en lib/utils (formatEur).
const formatCurrency = formatEur;

const nfPct = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatPctPlain = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return `${nfPct.format(value)}%`;
};

const formatPctSigned = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${nfPct.format(value)}%`;
};

// Lee un valor de un mapa {anio: valor} tolerando claves string o number
const getAnio = (map, anio) => {
  if (!map) return null;
  const v = map[anio] ?? map[String(anio)];
  return v === undefined ? null : v;
};

// ---------------------------------------------------------------------------
// KPI card: último año real → primer año proyectado
// ---------------------------------------------------------------------------

function KpiCard({ titulo, icon: Icon, valorHist, valorProy, anioHist, anioProy, deltaPct, subHist, subProy, invertirSemantica = false, formato = "eur" }) {
  const fmt = formato === "pct" ? formatPctPlain : formatCurrency;
  const tieneDelta = deltaPct !== null && deltaPct !== undefined && !isNaN(deltaPct);
  const positivoBueno = invertirSemantica ? deltaPct < 0 : deltaPct > 0;
  const DeltaIcon = tieneDelta && deltaPct >= 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{anioHist}</div>
            <div className="text-lg font-semibold font-mono tabular-nums">{fmt(valorHist)}</div>
            {subHist && <div className="text-[11px] text-muted-foreground">{subHist}</div>}
          </div>
          <div className="text-muted-foreground pb-1">→</div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-primary">{anioProy}</div>
            <div className="text-lg font-semibold font-mono tabular-nums text-primary">{fmt(valorProy)}</div>
            {subProy && <div className="text-[11px] text-primary/80">{subProy}</div>}
          </div>
        </div>
        {tieneDelta && (
          <div
            className={cn(
              "mt-2 inline-flex items-center gap-0.5 text-xs font-medium",
              deltaPct === 0
                ? "text-muted-foreground"
                : positivoBueno
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400"
            )}
          >
            <DeltaIcon className="h-3.5 w-3.5" />
            {formatPctSigned(deltaPct)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cabecera de tabla con zonas Histórico / Proyección (reutilizable)
// ---------------------------------------------------------------------------

// Todas las tablas por años de esta vista (y del Resumen, que la incrusta)
// comparten rejilla: concepto 260 px y COL_ANIO_MONO por año (importes con
// céntimos en monoespaciada), para que queden alineadas unas con otras.
const ANCHO_ANIO_RESULTADOS = COL_ANIO_MONO;

function CabeceraZonas({ aniosHist, aniosProy, labelCol = "Partida" }) {
  return (
    <>
    <ColsAnios n={aniosHist.length + aniosProy.length} anio={ANCHO_ANIO_RESULTADOS} />
    <TableHeader>
      <TableRow className="border-b-0 hover:bg-transparent">
        <TableHead className="h-7" />
        {aniosHist.length > 0 && (
          <TableHead
            colSpan={aniosHist.length}
            className="h-7 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground bg-muted/30"
          >
            Histórico
          </TableHead>
        )}
        {aniosProy.length > 0 && (
          <TableHead
            colSpan={aniosProy.length}
            className="h-7 text-center text-[11px] font-semibold uppercase tracking-widest text-primary bg-primary/5 border-l-2 border-primary/40"
          >
            Proyección
          </TableHead>
        )}
      </TableRow>
      <TableRow className="bg-muted/30">
        <TableHead>{labelCol}</TableHead>
        {aniosHist.map((anio) => (
          <TableHead key={anio} className="text-right bg-muted/30">
            {anio}
          </TableHead>
        ))}
        {aniosProy.map((anio, i) => (
          <TableHead
            key={anio}
            className={cn(
              "text-right bg-primary/5 text-primary font-semibold",
              i === 0 && "border-l-2 border-primary/40"
            )}
          >
            {anio}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
    </>
  );
}

// Celdas de una fila con zonas hist/proy
function CeldasZonas({ aniosHist, aniosProy, porAnio, fmt = formatCurrency, claseExtra }) {
  return (
    <>
      {aniosHist.map((anio) => (
        <TableCell key={anio} className={cn("text-right font-mono text-sm tabular-nums", claseExtra)}>
          {fmt(getAnio(porAnio, anio))}
        </TableCell>
      ))}
      {aniosProy.map((anio, i) => (
        <TableCell
          key={anio}
          className={cn(
            "text-right font-mono text-sm tabular-nums bg-primary/5",
            i === 0 && "border-l-2 border-primary/40",
            claseExtra
          )}
        >
          {fmt(getAnio(porAnio, anio))}
        </TableCell>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Badge OK / Error de un check de cuadre (diferencia ~0 → OK)
// ---------------------------------------------------------------------------

function BadgeCheck({ diferencia, fmt = formatCurrency }) {
  if (diferencia == null) return <span className="text-xs text-muted-foreground">—</span>;
  const cuadra = Math.abs(diferencia) < 1;
  return cuadra ? (
    <Badge
      variant="outline"
      className="text-[11px] gap-1 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800"
    >
      <CheckCircle2 className="h-3 w-3" />
      OK
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="text-[11px] gap-1 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-800"
    >
      <XCircle className="h-3 w-3" />
      {fmt(diferencia)}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Parámetros del modelo, SOLO LECTURA (petición de Pau, nov-2026): impuesto,
// IPC, payout y nivel se editan en Drivers → Generales; la NOF, en su pestaña.
// Aquí queda una línea con los valores vigentes y el enlace para editarlos.
// ---------------------------------------------------------------------------

const fmtPctParam = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} %`);

function ParametrosResumen({ config, empresaId, readOnly }) {
  if (!config) return null;
  const nivel = NIVEL_PROYECCION_LABEL[config.nivel_proyeccion || "auto"] || config.nivel_proyeccion;
  return (
    <p className="text-xs text-muted-foreground" data-testid="parametros-resumen">
      Impuesto {fmtPctParam(config.tasa_impuestos_pct)} · IPC {config.inflacion_pct != null ? fmtPctParam(config.inflacion_pct) : "sin inflación"} ·
      Payout {fmtPctParam(config.payout_pct)} · Nivel {nivel} —{" "}
      <Link
        to={`/empresas/${empresaId}/proyeccion?tab=generales`}
        className="text-primary hover:underline"
        data-testid="parametros-ir-generales"
      >
        {readOnly ? "Ver en Generales" : "Editar en Generales"}
      </Link>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const PYG_DESTACADAS = new Set(["EBITDA", "EBIT", "Resultado neto"]);

// Health de la proyección: ¿cuadra todo (Activo=Pasivo, Resultado=PN,
// CashFlow=Tesorería) y qué riesgos hay? Lo alimenta resultado.salud (backend).
function SaludProyeccion({ salud }) {
  if (!salud) return null;
  const estado = salud.estado || "ok";
  const cfg =
    {
      ok: {
        Icon: CheckCircle2,
        cls: "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/40",
        titulo: "Proyección cuadrada",
        sub: "PyG, balance y flujo de caja concilian en todos los años.",
      },
      avisos: {
        Icon: AlertTriangle,
        cls: "border-amber-300 bg-amber-50/60 dark:bg-amber-950/40",
        titulo: "Cuadra, con avisos a revisar",
        sub: "Los estados concilian, pero hay supuestos/riesgos que conviene revisar.",
      },
      error: {
        Icon: XCircle,
        cls: "border-rose-300 bg-rose-50/60 dark:bg-rose-950/40",
        titulo: "Descuadre detectado",
        sub: "Algún estado no concilia: revisa los checks marcados en rojo.",
      },
    }[estado] || {};
  const Icon = cfg.Icon || CheckCircle2;
  const checks = salud.checks || {};
  const filas = [
    { k: "activo_pasivo", label: "Total Activo = Total Pasivo + PN" },
    { k: "pyg_historico", label: "Resultado del modelo = Resultado contable (histórico)" },
    { k: "resultado_pn", label: "Resultado del ejercicio = Resultado en Patrimonio Neto" },
    { k: "cashflow_tesoreria", label: "Cash Flow Total = Variación de Tesorería" },
  ].filter((fr) => checks[fr.k]);
  return (
    <Card className={cn("border", cfg.cls)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-5 w-5 shrink-0" />
          Salud de la proyección — {cfg.titulo}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{cfg.sub}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          {filas.map(({ k, label }) => {
            const c = checks[k] || {};
            return (
              <div key={k} className="flex items-center justify-between text-sm gap-3">
                <span className="flex items-center gap-2">
                  {c.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-rose-600 shrink-0" />
                  )}
                  {label}
                </span>
                <span className="font-mono text-xs text-muted-foreground shrink-0">
                  dif. máx {formatCurrency(c.max_diferencia ?? 0)}
                </span>
              </div>
            );
          })}
        </div>
        {(salud.avisos || []).length > 0 && (
          <div className="space-y-1.5 pt-2 border-t">
            {salud.avisos.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <AlertTriangle
                  className={cn(
                    "h-3.5 w-3.5 mt-0.5 shrink-0",
                    a.severidad === "alta" ? "text-rose-500" : "text-amber-500"
                  )}
                />
                <div>
                  <p>{a.mensaje}</p>
                  {a.solucion && (
                    <p className="text-muted-foreground mt-0.5">
                      <span className="font-medium">Cómo arreglarlo: </span>
                      {a.solucion}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── EBITDA normalizado (punto 6 de Héctor, 3-oct-2026) ──────────────────────
// Add-backs manuales POR AÑO sobre el EBITDA proyectado, con el puente
// EBITDA → ajustes → EBITDA normalizado a la vista (espejo del EBITDA
// Ajustado histórico). Es capa de ANÁLISIS: el backend lo expone aparte y no
// toca el PyG, el balance ni la caja — los cuadres quedan intactos.
function PuenteEbitdaNormalizado({ resultado, config, aniosProy, readOnly, onGuardar }) {
  const [ajustes, setAjustes] = useState([]);
  const [editado, setEditado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setAjustes(
      (config?.ebitda_normalizado?.ajustes || []).map((a) => ({
        concepto: a.concepto || "",
        por_anio: { ...(a.por_anio || {}) },
      }))
    );
    setEditado(false);
  }, [config]);

  if (!aniosProy.length) return null;
  const ebitdaPa = resultado?.kpis?.ebitda?.por_anio || {};
  if (readOnly && ajustes.length === 0) return null;

  const num = (v) => {
    const x = parseFloat(String(v ?? "").replace(",", "."));
    return isNaN(x) ? 0 : x;
  };
  const totalAnio = (a) =>
    ajustes.reduce((s, aj) => s + num(aj.por_anio?.[String(a)]), 0);

  const guardar = async () => {
    setGuardando(true);
    try {
      const limpios = ajustes
        .filter((aj) => aj.concepto.trim())
        .map((aj) => ({
          concepto: aj.concepto.trim(),
          por_anio: Object.fromEntries(
            aniosProy
              .map((a) => [String(a), aj.por_anio?.[String(a)]])
              .filter(([, v]) => v !== "" && v != null && !isNaN(parseFloat(String(v).replace(",", "."))))
              .map(([a, v]) => [a, parseFloat(String(v).replace(",", "."))])
          ),
        }));
      await onGuardar(
        { ebitda_normalizado: { ajustes: limpios } },
        "Ajustes del EBITDA normalizado guardados"
      );
      setEditado(false);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Card data-testid="ebitda-normalizado">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          EBITDA normalizado (puente)
          <Badge variant="outline" className="text-[11px] font-normal">no toca el modelo</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 overflow-x-auto">
        {/* -mx-6 + pl-2 en la 1ª columna: arranca donde las demás tablas de la vista */}
        <div className="-mx-6 overflow-x-auto">
        <table className={cn(TABLA_ANIOS, "text-sm [&_tr>*:first-child]:pl-2")}>
          <ColsAnios n={aniosProy.length} anio={ANCHO_ANIO_RESULTADOS} despues={readOnly ? [] : [32]} />
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="text-left py-1.5 pr-3 font-medium">Concepto</th>
              {aniosProy.map((a) => (
                <th key={a} className="text-right py-1.5 px-2 font-mono font-medium text-primary whitespace-nowrap">
                  {a}<span className="ml-1 text-[10px] font-semibold uppercase rounded bg-proy-strong text-proy px-1 py-0.5 align-middle">Proy.</span>
                </th>
              ))}
              {!readOnly && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-1.5 pr-3 font-medium">EBITDA proyectado</td>
              {aniosProy.map((a) => (
                <td key={a} className="text-right py-1.5 px-2 font-mono whitespace-nowrap">
                  {formatEur(ebitdaPa[String(a)])}
                </td>
              ))}
              {!readOnly && <td></td>}
            </tr>
            {ajustes.map((aj, i) => (
              <tr key={i} className="border-b bg-amber-50/40 dark:bg-amber-950/10">
                <td className="py-1 pr-3">
                  {readOnly ? (
                    <span className="text-amber-700 dark:text-amber-400">{aj.concepto}</span>
                  ) : (
                    <Input
                      value={aj.concepto}
                      placeholder="Concepto del ajuste (p.ej. gasto no recurrente)"
                      className="h-7 text-xs"
                      onChange={(e) => {
                        const v = e.target.value;
                        setAjustes((s) => s.map((x, j) => (j === i ? { ...x, concepto: v } : x)));
                        setEditado(true);
                      }}
                      data-testid={`en-concepto-${i}`}
                    />
                  )}
                </td>
                {aniosProy.map((a) => (
                  <td key={a} className="text-right py-1 px-2">
                    {readOnly ? (
                      <span className="font-mono text-xs">{formatEur(num(aj.por_anio?.[String(a)]))}</span>
                    ) : (
                      <Input
                        type="number"
                        step="any"
                        placeholder="0"
                        className="h-7 text-right font-mono text-xs"
                        value={aj.por_anio?.[String(a)] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAjustes((s) => s.map((x, j) =>
                            j === i ? { ...x, por_anio: { ...x.por_anio, [String(a)]: v } } : x));
                          setEditado(true);
                        }}
                        data-testid={`en-importe-${i}-${a}`}
                      />
                    )}
                  </td>
                ))}
                {!readOnly && (
                  <td className="text-center">
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive"
                      title="Quitar ajuste"
                      onClick={() => { setAjustes((s) => s.filter((_, j) => j !== i)); setEditado(true); }}
                      data-testid={`en-borrar-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            <tr className="border-b text-amber-700 dark:text-amber-400">
              <td className="py-1.5 pr-3">Ajustes de normalización</td>
              {aniosProy.map((a) => (
                <td key={a} className="text-right py-1.5 px-2 font-mono whitespace-nowrap">
                  {totalAnio(a) >= 0 ? "+" : ""}{formatEur(totalAnio(a))}
                </td>
              ))}
              {!readOnly && <td></td>}
            </tr>
            <tr className="font-bold bg-primary/5">
              <td className="py-1.5 pr-3">= EBITDA normalizado</td>
              {aniosProy.map((a) => (
                <td key={a} className="text-right py-1.5 px-2 font-mono whitespace-nowrap">
                  {formatEur((ebitdaPa[String(a)] || 0) + totalAnio(a))}
                </td>
              ))}
              {!readOnly && <td></td>}
            </tr>
          </tbody>
        </table>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs"
              onClick={() => { setAjustes((s) => [...s, { concepto: "", por_anio: {} }]); setEditado(true); }}
              data-testid="en-anadir">
              <Plus className="h-3.5 w-3.5" /> Añadir ajuste
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={guardar}
              disabled={!editado || guardando} data-testid="en-guardar">
              {guardando ? "Guardando…" : "Guardar ajustes"}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Los ajustes NO tocan el PyG, el balance ni la caja: solo el puente de análisis.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ResultadosProyeccionContent({ empresaId, readOnly = false, soloPanel = false }) {
  const [resultado, setResultado] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supuestosAbierto, setSupuestosAbierto] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [dfn, setDfn] = useState(null);

  const cargar = useCallback(
    async (inicial = false) => {
      try {
        if (inicial) setLoading(true);
        setError(null);
        const [resResultado, resConfig, resDfn] = await Promise.all([
          getProyeccionResultado(empresaId),
          getProyeccionConfig(empresaId),
          // Fila DFN del Resumen (fase 3 de PLAN_DFN.md): misma fuente que
          // la página DFN y las Comparativas; tolerante (sin DFN, sin fila).
          getDfn(empresaId).catch(() => ({ data: null })),
        ]);
        setResultado(resResultado.data);
        setConfig(resConfig.data);
        setDfn(resDfn.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Error al cargar el resultado de la proyección");
      } finally {
        if (inicial) setLoading(false);
      }
    },
    [empresaId]
  );

  useEffect(() => {
    cargar(true);
  }, [cargar]);

  // Exporta el modelo completo a Excel (también disponible en solo-lectura)
  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const response = await exportarProyeccion(empresaId);
      const anios = resultado?.anios_proyectados || [];
      const sufijo =
        anios.length > 0 ? `_${anios[0]}-${anios[anios.length - 1]}` : "";
      downloadBlob(response.data, `Proyeccion${sufijo}.xlsx`);
      toast.success("Proyección exportada correctamente");
    } catch (e) {
      toast.error(await parseBlobError(e, "No se pudo exportar la proyección"));
    } finally {
      setExportando(false);
    }
  };

  // PUT parcial de config + recarga del resultado (EBITDA normalizado)
  const handleConfigGuardar = async (parcial, mensaje = "Parámetros actualizados") => {
    try {
      await saveProyeccionConfig(empresaId, parcial);
      toast.success(mensaje);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar los parámetros");
    }
  };

  const aniosHist = useMemo(
    () => [...(resultado?.anios_historicos || [])].sort((a, b) => a - b),
    [resultado]
  );
  const aniosProy = useMemo(
    () => [...(resultado?.anios_proyectados || [])].sort((a, b) => a - b),
    [resultado]
  );

  const ultimoHist = aniosHist[aniosHist.length - 1];
  const primerProy = aniosProy[0];

  const kpis = resultado?.kpis;

  // Delta % client-side (último hist → primer proy) para EBITDA y Gastos
  const deltaPct = (porAnio) => {
    const vh = getAnio(porAnio, ultimoHist);
    const vp = getAnio(porAnio, primerProy);
    if (vh === null || vp === null || vh === 0) return null;
    return ((vp - vh) / Math.abs(vh)) * 100;
  };

  // Datos del gráfico: barras ventas (hist/proy) + línea margen EBITDA %
  const chartData = useMemo(() => {
    if (!resultado) return [];
    return [...aniosHist, ...aniosProy].map((anio) => {
      const esHist = aniosHist.includes(anio);
      const ventas = getAnio(kpis?.ventas?.por_anio, anio);
      return {
        anio: String(anio),
        ventasHist: esHist ? ventas : null,
        ventasProy: esHist ? null : ventas,
        margenEbitda: getAnio(kpis?.ebitda?.margen_por_anio, anio),
      };
    });
  }, [resultado, aniosHist, aniosProy, kpis]);

  // -------------------------------------------------------------------------
  // Balance: años hist/proy de balance.anios y partidas agrupadas
  // -------------------------------------------------------------------------
  const balance = resultado?.balance;
  const proySet = useMemo(() => new Set(aniosProy.map(Number)), [aniosProy]);

  const balanceAniosHist = useMemo(
    () =>
      (balance?.anios || [])
        .map(Number)
        .filter((a) => !proySet.has(a))
        .sort((a, b) => a - b),
    [balance, proySet]
  );
  const balanceAniosProy = useMemo(
    () =>
      (balance?.anios || [])
        .map(Number)
        .filter((a) => proySet.has(a))
        .sort((a, b) => a - b),
    [balance, proySet]
  );

  const partidasActivo = useMemo(
    () => (balance?.partidas || []).filter((p) => p.grupo === "activo"),
    [balance]
  );
  const partidasPasivoPn = useMemo(
    () => (balance?.partidas || []).filter((p) => p.grupo === "pasivo_pn"),
    [balance]
  );

  // Cuadre por año proyectado: {anio: diferencia}
  const cuadrePorAnio = useMemo(() => {
    const map = {};
    (resultado?.check_cuadre || []).forEach((c) => {
      map[Number(c.anio)] = c.diferencia;
    });
    return map;
  }, [resultado]);

  // R-11c: checks adicionales por año proyectado.
  // (a) Resultado del ejercicio (PyG) == Resultado reflejado en PN
  const resultadoPnPorAnio = useMemo(() => {
    const map = {};
    (resultado?.check_resultado_pn || []).forEach((c) => {
      map[Number(c.anio)] = c.diferencia;
    });
    return map;
  }, [resultado]);
  // (b) Cash Flow Total == variación de la Tesorería del activo
  const cashflowTesoreriaPorAnio = useMemo(() => {
    const map = {};
    (resultado?.check_cashflow_tesoreria || []).forEach((c) => {
      map[Number(c.anio)] = c.diferencia;
    });
    return map;
  }, [resultado]);

  // Conciliación REAL (años históricos): el resultado del modelo contra el
  // resultado CONTABLE que calcula generar_balance. Es el único check que puede
  // fallar de verdad; los otros dos son consistencia interna (0 por construcción).
  const pygHistoricoPorAnio = useMemo(() => {
    const map = {};
    (resultado?.check_pyg_historico || []).forEach((c) => {
      map[Number(c.anio)] = c.diferencia;
    });
    return map;
  }, [resultado]);

  const hayChecksExtra =
    (resultado?.check_resultado_pn || []).length > 0 ||
    (resultado?.check_cashflow_tesoreria || []).length > 0;
  const hayCheckHist = (resultado?.check_pyg_historico || []).length > 0;

  // -------------------------------------------------------------------------
  // Flujo de caja proyectado
  // -------------------------------------------------------------------------
  const flujo = useMemo(
    () => [...(resultado?.flujo_proyectado || [])].sort((a, b) => a.anio - b.anio),
    [resultado]
  );

  // Fila Dividendos solo si el backend ya la envía con algún valor ≠ 0
  const hayDividendos = flujo.some((f) => f.dividendos != null && f.dividendos !== 0);

  const FLUJO_FILAS = [
    { clave: "cfo", label: "CFO (explotación)", colorear: false },
    { clave: "cfi", label: "CFI (inversión)", colorear: true },
    { clave: "cff", label: "CFF (financiación)", colorear: true },
    ...(hayDividendos
      ? [{ clave: "dividendos", label: "Dividendos", colorear: true }]
      : []),
    { clave: "delta_tesoreria", label: "Δ Tesorería", colorear: false },
    { clave: "tesoreria_final", label: "Tesorería final", colorear: false, destacada: true },
  ];

  // -------------------------------------------------------------------------
  // Gráfico Deuda total vs Tesorería (años proyectados)
  // -------------------------------------------------------------------------
  const deudaTesoreriaData = useMemo(() => {
    if (!resultado) return [];
    const partidasDeuda = (balance?.partidas || []).filter(
      (p) => p.grupo === "pasivo_pn" && /deuda|pr[eé]stamo/i.test(p.concepto || "")
    );
    return aniosProy.map((anio) => {
      const deuda = partidasDeuda.reduce((acc, p) => {
        const v = getAnio(p.por_anio, anio);
        return acc + (v || 0);
      }, 0);
      const f = flujo.find((x) => Number(x.anio) === Number(anio));
      return {
        anio: String(anio),
        deuda: partidasDeuda.length > 0 ? deuda : null,
        tesoreria: f ? f.tesoreria_final : null,
      };
    });
  }, [resultado, balance, aniosProy, flujo]);

  const hayDeudaTesoreria = deudaTesoreriaData.some(
    (d) => d.deuda !== null || d.tesoreria !== null
  );

  // -------------------------------------------------------------------------
  // R-11a: ratios proyectados derivados del PyG y balance ya producidos.
  // Solo ratios calculables sin datos extra (conservador): margen neto, ROE,
  // endeudamiento y Deuda/EBITDA. Mismo formato visual de zonas.
  // -------------------------------------------------------------------------
  const ratios = useMemo(() => {
    if (!resultado) return [];
    const pygMap = {};
    (resultado.pyg_completo || []).forEach((f) => {
      pygMap[f.concepto] = f.por_anio || {};
    });
    const balMap = {};
    (balance?.partidas || []).forEach((p) => {
      // ignora la fila informativa "Resultado del ejercicio" (sub)
      if (!p.sub) balMap[`${p.grupo}:${p.concepto}`] = p.por_anio || {};
    });
    const neto = pygMap["Resultado neto"] || {};
    const ebitda = kpis?.ebitda?.por_anio || {};
    const pn = balMap["pasivo_pn:Patrimonio neto"] || {};
    const deudaLp = balMap["pasivo_pn:Deuda financiera LP"] || {};
    const deudaCp = balMap["pasivo_pn:Deuda financiera CP"] || {};
    const ventas = kpis?.ventas?.por_anio || {};

    const todos = [...aniosHist, ...aniosProy];
    // `soloPositivo`: N/D si el denominador es ≤ 0 — la regla del dashboard
    // (domain/dashboard.py) para ROE (PN ≤ 0) y los múltiplos sobre EBITDA.
    const ratio = (num, den, factor = 1, soloPositivo = false) => {
      const out = {};
      todos.forEach((a) => {
        const n = getAnio(num, a);
        const d = getAnio(den, a);
        const valido = d != null && (soloPositivo ? d > 0 : d !== 0);
        out[a] = n != null && valido ? (n / d) * factor : null;
      });
      return out;
    };
    const deudaTotal = {};
    todos.forEach((a) => {
      const lp = getAnio(deudaLp, a);
      const cp = getAnio(deudaCp, a);
      deudaTotal[a] = lp != null || cp != null ? (lp || 0) + (cp || 0) : null;
    });

    // DFN del backend (domain/dfn.py), definición del Dashboard: deuda LP +
    // CP − tesorería; múltiplo sobre el EBITDA ajustado (N/D con EBITDA ≤ 0).
    const serieDfn = (clave) => {
      const out = {};
      todos.forEach((a) => {
        out[a] = dfn?.filas?.[clave]?.[String(a)] ?? null;
      });
      return out;
    };
    const filasDfn = dfn?.filas
      ? [
          { concepto: "DFN (deuda financiera − tesorería)", por_anio: serieDfn("dfn_balance"), pct: false },
          { concepto: "DFN / EBITDA ajustado", por_anio: serieDfn("dfn_balance_ebitda_ajustado"), pct: false, veces: true },
        ]
      : [];

    return [
      { concepto: "Margen neto %", por_anio: ratio(neto, ventas, 100, true), pct: true },
      { concepto: "ROE % (neto / PN)", por_anio: ratio(neto, pn, 100, true), pct: true },
      ...filasDfn,
      {
        concepto: "Endeudamiento (Deuda / PN)",
        por_anio: ratio(deudaTotal, pn, 1, true),
        pct: false,
        veces: true,
      },
      {
        concepto: "Deuda / EBITDA",
        por_anio: ratio(deudaTotal, ebitda, 1, true),
        pct: false,
        veces: true,
      },
    ];
  }, [resultado, balance, kpis, aniosHist, aniosProy, dfn]);

  const hayRatios = ratios.some((r) =>
    Object.values(r.por_anio).some((v) => v != null)
  );

  const fmtVeces = (v) => {
    if (v === null || v === undefined || isNaN(v)) return "—";
    return `${nfPct.format(v)}×`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !resultado) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error || "Sin datos de proyección"}</p>
        <p className="text-xs mt-1">Configura drivers en las pestañas de Ingresos y Gastos para ver resultados</p>
      </div>
    );
  }

  const pygCompleto = resultado.pyg_completo || [];

  // Bloque de supuestos, compartido entre la vista completa (pestaña Ratios)
  // y el modo panel.
  const bloqueSupuestos = (resultado.supuestos || []).length > 0 && (
    <Collapsible open={supuestosAbierto} onOpenChange={setSupuestosAbierto}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer select-none">
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              Supuestos aplicados
              <Badge variant="outline" className="text-[11px] font-normal">
                {resultado.supuestos.length}
              </Badge>
              <ChevronDown
                className={cn(
                  "h-4 w-4 ml-auto text-muted-foreground transition-transform",
                  supuestosAbierto && "rotate-180"
                )}
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
              {resultado.supuestos.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );

  return (
    <div className="space-y-6" data-testid="resultados-proyeccion">
      {/* Health: cuadre global de la proyección + avisos */}
      <SaludProyeccion salud={resultado.salud} />

      {/* Header de la pestaña: parámetros vigentes (solo lectura) + export */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Resultados consolidados: PyG, balance y tesorería proyectados.
          </p>
          <ParametrosResumen config={config} empresaId={empresaId} readOnly={readOnly} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={handleExportarExcel}
            disabled={exportando}
            data-testid="exportar-proyeccion-btn"
          >
            {exportando ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          titulo="Ventas"
          icon={TrendingUp}
          anioHist={ultimoHist}
          anioProy={primerProy}
          valorHist={getAnio(kpis?.ventas?.por_anio, ultimoHist)}
          valorProy={getAnio(kpis?.ventas?.por_anio, primerProy)}
          deltaPct={kpis?.ventas?.delta_pct_primer_anio ?? deltaPct(kpis?.ventas?.por_anio)}
        />
        <KpiCard
          titulo="EBITDA"
          icon={Wallet}
          anioHist={ultimoHist}
          anioProy={primerProy}
          valorHist={getAnio(kpis?.ebitda?.por_anio, ultimoHist)}
          valorProy={getAnio(kpis?.ebitda?.por_anio, primerProy)}
          deltaPct={deltaPct(kpis?.ebitda?.por_anio)}
          subHist={`margen ${formatPctPlain(getAnio(kpis?.ebitda?.margen_por_anio, ultimoHist))}`}
          subProy={`margen ${formatPctPlain(getAnio(kpis?.ebitda?.margen_por_anio, primerProy))}`}
        />
        <KpiCard
          titulo="Gastos"
          icon={Percent}
          anioHist={ultimoHist}
          anioProy={primerProy}
          valorHist={getAnio(kpis?.gastos?.por_anio, ultimoHist)}
          valorProy={getAnio(kpis?.gastos?.por_anio, primerProy)}
          deltaPct={deltaPct(kpis?.gastos?.por_anio)}
          invertirSemantica
          subHist={`${formatPctPlain(getAnio(kpis?.gastos?.pct_sobre_ventas_por_anio, ultimoHist))} s/ventas`}
          subProy={`${formatPctPlain(getAnio(kpis?.gastos?.pct_sobre_ventas_por_anio, primerProy))} s/ventas`}
        />
      </div>

      {/* Avisos */}
      {(kpis?.avisos || []).length > 0 && (
        <Alert className="border-primary/50 bg-primary/5 [&>svg]:text-primary py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs space-y-0.5">
            {kpis.avisos.map((aviso, i) => (
              <p key={i}>{aviso}</p>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Punto 6 de Héctor (3-oct): puente EBITDA → ajustes → EBITDA
          normalizado, editable aquí y visible (solo lectura) en Resumen. */}
      <PuenteEbitdaNormalizado
        resultado={resultado}
        config={config}
        aniosProy={aniosProy}
        readOnly={readOnly}
        onGuardar={handleConfigGuardar}
      />

      {/* Modo PANEL (Drivers → Resultados, quejas Pau 30-09 tanda 2): el
          detalle completo (PyG, balance, flujos, ratios) ya vive en las 3
          Comparativas con histórico + proyectado, así que aquí no se duplica:
          quedan salud, KPIs, parámetros, export, supuestos y los accesos.
          El Resumen de Análisis sigue montando la vista completa. */}
      {soloPanel && (
        <>
          {bloqueSupuestos}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Ver el detalle completo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <Link to={`/empresas/${empresaId}/comparativa-anios`} data-testid="link-comparativa-anios">
                    Comparativa Años
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <Link to={`/empresas/${empresaId}/comparativa-flujos`} data-testid="link-comparativa-flujos">
                    Flujos de Caja
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <Link to={`/empresas/${empresaId}/comparativa`} data-testid="link-comparativa-ratios">
                    Comparativa Ratios
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Cada comparativa muestra el histórico y la proyección juntos, con los
                años proyectados marcados en amarillo "Proy.".
              </p>
            </CardContent>
          </Card>
        </>
      )}

      {/* Punto 7: las 3 vistas a futuro, espejo de las históricas (Comparativa
          de Años, Flujos de Caja, Comparativa de Ratios). La Salud (3 checks) y
          los KPIs quedan arriba, siempre visibles. */}
      {!soloPanel && (
      <Tabs defaultValue="anios" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="anios">Comparativa de Años</TabsTrigger>
          <TabsTrigger value="flujos">Flujos de Caja</TabsTrigger>
          <TabsTrigger value="ratios">Comparativa de Ratios</TabsTrigger>
        </TabsList>

        <TabsContent value="anios" className="space-y-6">
      {/* Tabla PyG proyectada (W19.3: antes "PyG consolidado") */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>PyG proyectado</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table className={TABLA_ANIOS}>
            <CabeceraZonas aniosHist={aniosHist} aniosProy={aniosProy} />
            <TableBody>
              {(resultado.pyg || []).map((fila, idx) => (
                <TableRow key={idx} className="hover:bg-muted/20">
                  <TableCell className="font-medium">{fila.partida}</TableCell>
                  <CeldasZonas aniosHist={aniosHist} aniosProy={aniosProy} porAnio={fila.por_anio} />
                </TableRow>
              ))}
              {/* Fila EBITDA destacada */}
              <TableRow className="border-t-2 font-semibold bg-primary/5 hover:bg-primary/10">
                <TableCell>EBITDA</TableCell>
                {aniosHist.map((anio) => (
                  <TableCell key={anio} className="text-right py-2">
                    <div className="font-mono text-sm tabular-nums">
                      {formatCurrency(getAnio(kpis?.ebitda?.por_anio, anio))}
                    </div>
                    <div className="text-[11px] font-normal text-muted-foreground">
                      {formatPctPlain(getAnio(kpis?.ebitda?.margen_por_anio, anio))} margen
                    </div>
                  </TableCell>
                ))}
                {aniosProy.map((anio, i) => (
                  <TableCell
                    key={anio}
                    className={cn("text-right py-2 bg-primary/10", i === 0 && "border-l-2 border-primary/40")}
                  >
                    <div className="font-mono text-sm tabular-nums">
                      {formatCurrency(getAnio(kpis?.ebitda?.por_anio, anio))}
                    </div>
                    <div className="text-[11px] font-normal text-primary">
                      {formatPctPlain(getAnio(kpis?.ebitda?.margen_por_anio, anio))} margen
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* PyG proyectado completo */}
      {pygCompleto.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>PyG proyectado completo</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className={TABLA_ANIOS}>
              <CabeceraZonas aniosHist={aniosHist} aniosProy={aniosProy} labelCol="Concepto" />
              <TableBody>
                {pygCompleto.map((fila, idx) => {
                  const esMargen = /margen/i.test(fila.concepto || "");
                  const destacada = PYG_DESTACADAS.has(fila.concepto);
                  return (
                    <TableRow
                      key={idx}
                      className={cn(
                        "hover:bg-muted/20",
                        destacada && "font-semibold bg-primary/5 hover:bg-primary/10 border-t",
                        esMargen && "text-muted-foreground border-t"
                      )}
                    >
                      <TableCell className={cn("font-medium", esMargen && "font-normal text-xs")}>
                        {fila.concepto}
                      </TableCell>
                      <CeldasZonas
                        aniosHist={aniosHist}
                        aniosProy={aniosProy}
                        porAnio={fila.por_anio}
                        fmt={esMargen ? formatPctPlain : formatCurrency}
                        claseExtra={esMargen ? "text-xs text-muted-foreground" : undefined}
                      />
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Balance proyectado */}
      {balance && (balance.partidas || []).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Balance proyectado</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className={TABLA_ANIOS}>
              <CabeceraZonas
                aniosHist={balanceAniosHist}
                aniosProy={balanceAniosProy}
                labelCol="Concepto"
              />
              <TableBody>
                {/* Activo */}
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell
                    colSpan={1 + balanceAniosHist.length + balanceAniosProy.length}
                    className="py-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                  >
                    Activo
                  </TableCell>
                </TableRow>
                {partidasActivo.map((fila, idx) => {
                  const esTesoreria = /tesorer/i.test(fila.concepto || "");
                  return (
                    <TableRow
                      key={`a-${idx}`}
                      className={cn(
                        "hover:bg-muted/20",
                        esTesoreria && "font-semibold bg-primary/5 hover:bg-primary/10"
                      )}
                    >
                      <TableCell
                        className={cn(
                          "font-medium pl-6",
                          fila.sub && "pl-12 font-normal text-muted-foreground"
                        )}
                      >
                        {fila.concepto}
                      </TableCell>
                      <CeldasZonas
                        aniosHist={balanceAniosHist}
                        aniosProy={balanceAniosProy}
                        porAnio={fila.por_anio}
                        claseExtra={fila.sub ? "text-muted-foreground" : undefined}
                      />
                    </TableRow>
                  );
                })}

                {/* Patrimonio neto y Pasivo */}
                <TableRow className="bg-muted/40 hover:bg-muted/40 border-t-2">
                  <TableCell
                    colSpan={1 + balanceAniosHist.length + balanceAniosProy.length}
                    className="py-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                  >
                    Patrimonio neto y Pasivo
                  </TableCell>
                </TableRow>
                {partidasPasivoPn.map((fila, idx) => (
                  <TableRow key={`p-${idx}`} className="hover:bg-muted/20">
                    <TableCell
                      className={cn(
                        "font-medium pl-6",
                        fila.sub && "pl-12 font-normal text-muted-foreground"
                      )}
                    >
                      {fila.concepto}
                    </TableCell>
                    <CeldasZonas
                      aniosHist={balanceAniosHist}
                      aniosProy={balanceAniosProy}
                      porAnio={fila.por_anio}
                      claseExtra={fila.sub ? "text-muted-foreground" : undefined}
                    />
                  </TableRow>
                ))}

                {/* Checks de cuadre por columna proyectada (R-11c) */}
                {[
                  {
                    label: "Cuadre Activo = PN + Pasivo",
                    map: cuadrePorAnio,
                    borde: true,
                  },
                  ...(hayCheckHist
                    ? [
                        {
                          label: "Resultado del modelo = Resultado contable",
                          title:
                            "Conciliación real: contrasta el resultado del PyG del modelo contra el resultado contable del balance (fuente independiente). Es el único check que puede fallar.",
                          map: pygHistoricoPorAnio,
                          zona: "hist",
                        },
                      ]
                    : []),
                  ...(hayChecksExtra
                    ? [
                        {
                          label: "Resultado PyG = Resultado en PN",
                          title:
                            "Consistencia interna: el PN se construye acumulando el resultado del modelo, así que esta diferencia es 0 por construcción.",
                          map: resultadoPnPorAnio,
                          interno: true,
                        },
                        {
                          label: "Cash Flow total = Δ Tesorería",
                          title:
                            "Consistencia interna: la tesorería es la partida de cierre del flujo, así que esta diferencia es 0 por construcción.",
                          map: cashflowTesoreriaPorAnio,
                          interno: true,
                        },
                      ]
                    : []),
                ].map(({ label, title, map, borde, zona, interno }, fila) => (
                  <TableRow
                    key={label}
                    className={cn(
                      "hover:bg-transparent",
                      (borde || fila === 0) && "border-t-2"
                    )}
                  >
                    <TableCell
                      className="text-xs font-medium text-muted-foreground"
                      title={title}
                    >
                      {label}
                      {interno && (
                        <span className="ml-1 text-[10px] opacity-60">(interno)</span>
                      )}
                    </TableCell>
                    {balanceAniosHist.map((anio) =>
                      zona === "hist" ? (
                        <TableCell key={anio} className="text-right">
                          <BadgeCheck diferencia={map[anio]} />
                        </TableCell>
                      ) : (
                        <TableCell key={anio} />
                      )
                    )}
                    {balanceAniosProy.map((anio, i) =>
                      zona === "hist" ? (
                        <TableCell
                          key={anio}
                          className={cn(
                            "bg-primary/5",
                            i === 0 && "border-l-2 border-primary/40"
                          )}
                        />
                      ) : (
                        <TableCell
                          key={anio}
                          className={cn(
                            "text-right bg-primary/5",
                            i === 0 && "border-l-2 border-primary/40"
                          )}
                        >
                          <BadgeCheck diferencia={map[anio]} />
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        <TabsContent value="flujos" className="space-y-6">
      {/* Flujo de caja proyectado */}
      {flujo.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Flujo de caja proyectado</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className={TABLA_ANIOS}>
              <ColsAnios n={flujo.length} anio={ANCHO_ANIO_RESULTADOS} />
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Concepto</TableHead>
                  {flujo.map((f) => (
                    <TableHead
                      key={f.anio}
                      className="text-right bg-primary/5 text-primary font-semibold"
                    >
                      {f.anio}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {FLUJO_FILAS.map(({ clave, label, colorear, destacada }) => (
                  <TableRow
                    key={clave}
                    className={cn(
                      "hover:bg-muted/20",
                      destacada && "font-semibold bg-primary/5 hover:bg-primary/10 border-t-2"
                    )}
                  >
                    <TableCell className="font-medium py-2">{label}</TableCell>
                    {flujo.map((f) => {
                      const v = f[clave];
                      return (
                        <TableCell
                          key={f.anio}
                          className={cn(
                            "text-right font-mono text-sm tabular-nums py-2 bg-primary/5",
                            colorear &&
                              v != null &&
                              (v < 0
                                ? "text-rose-600 dark:text-rose-400"
                                : v > 0
                                ? "text-emerald-600 dark:text-emerald-400"
                                : undefined)
                          )}
                        >
                          {formatCurrency(v)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        <TabsContent value="ratios" className="space-y-6">
      {/* Ratios proyectados (R-11a) */}
      {hayRatios && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Ratios proyectados</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table className={TABLA_ANIOS}>
              <CabeceraZonas aniosHist={aniosHist} aniosProy={aniosProy} labelCol="Ratio" />
              <TableBody>
                {ratios.map((fila, idx) => (
                  <TableRow key={idx} className="hover:bg-muted/20">
                    <TableCell className="font-medium">{fila.concepto}</TableCell>
                    <CeldasZonas
                      aniosHist={aniosHist}
                      aniosProy={aniosProy}
                      porAnio={fila.por_anio}
                      fmt={fila.pct ? formatPctPlain : fila.veces ? fmtVeces : formatCurrency}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="px-4 py-2 text-[11px] text-muted-foreground">
              Ratios derivados del PyG y el balance proyectados. ROE y endeudamiento usan
              el patrimonio neto de cierre; Deuda/EBITDA la deuda financiera total. La DFN
              (deuda financiera − tesorería) y su múltiplo vienen del módulo DFN, la misma
              fuente que el Dashboard y las Comparativas.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Supuestos aplicados */}
      {bloqueSupuestos}

      {/* Gráfico Ventas + margen EBITDA */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Ventas y margen EBITDA</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="anio" {...axisProps} />
                <YAxis
                  yAxisId="left"
                  {...axisProps}
                  tickFormatter={fmtCompact}
                  width={56}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...axisProps}
                  tickFormatter={(v) => `${nfPct.format(v)}%`}
                />
                <Tooltip
                  cursor={barCursor}
                  content={<ChartTooltip formatter={(value, name) => {
                    if (name === "Margen EBITDA") return [formatPctPlain(value), name];
                    return [formatCurrency(value), name];
                  }} />}
                />
                <Legend content={<ChartLegend />} />
                <Bar
                  yAxisId="left"
                  dataKey="ventasHist"
                  name="Ventas (histórico)"
                  fill={GUNMETAL}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                  stackId="ventas"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
                <Bar
                  yAxisId="left"
                  dataKey="ventasProy"
                  name="Ventas (proyección)"
                  fill="hsl(var(--primary))"
                  stackId="ventas"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="margenEbitda"
                  name="Margen EBITDA"
                  stroke="hsl(var(--foreground))"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "hsl(var(--foreground))" }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico Deuda total vs Tesorería (proyección) */}
      {hayDeudaTesoreria && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Deuda total vs Tesorería (proyección)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={deudaTesoreriaData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="anio" {...axisProps} />
                  <YAxis
                    {...axisProps}
                    tickFormatter={fmtCompact}
                  width={56}
                  />
                  <Tooltip
                  cursor={lineCursor}
                  content={<ChartTooltip formatter={(value, name) => [formatCurrency(value), name]} />}
                  />
                  <Legend content={<ChartLegend />} />
                  <Line
                    type="monotone"
                    dataKey="deuda"
                    name="Deuda total"
                    stroke={GUNMETAL}
                    strokeWidth={2}
                    dot={{ r: 3, fill: GUNMETAL }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="tesoreria"
                    name="Tesorería"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "hsl(var(--primary))" }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
